export type EventMap = {
  // Game lifecycle
  'game:loading': { gameId: string };
  'game:loaded': { gameId: string; title: string };
  'game:unloaded': Record<string, never>;
  'game:scoreSubmit': { gameId: string; score: number; sessionMs?: number };
  'game:liveScoreUpdate': { gameId: string; score: number };
  'game:checkpointSaved': { gameId: string };
  'game:checkpointLoaded': { gameId: string };

  'duel:invite_received': { duel: any; challengerName: string };
  'duel:start': { game_id: string; challenger_id: string; opponent_id: string };
  'duel:opponent_score': { score: number };
  'duel:finished_by_opponent': { score: number; duelId?: number };
  'friends:updated': Record<string, never>;

  // Auth events
  'auth:login': { userId: string; username: string };
  'auth:logout': Record<string, never>;

  // Ad events
  'ad:interstitialShown': { gameId: string };
  'ad:interstitialClosed': { gameId: string };
  'ad:blocked': Record<string, never>;

  // App & Network events
  'app:offline': Record<string, never>;
  'app:online': Record<string, never>;
  'toast:show': { message: string; type?: 'info' | 'success' | 'warning' | 'error' };
};

export type Listener<T> = (payload: T) => void;

class EventBusClass {
  private listeners = new Map<string, Set<Listener<any>>>();

  on<K extends keyof EventMap>(event: K, fn: Listener<EventMap[K]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(fn);

    // Return unsubscribe function
    return () => {
      this.off(event, fn);
    };
  }

  off<K extends keyof EventMap>(event: K, fn: Listener<EventMap[K]>): void {
    this.listeners.get(event)?.delete(fn);
  }

  emit<K extends keyof EventMap>(event: K, payload: EventMap[K]): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((fn) => {
        try {
          fn(payload);
        } catch (err) {
          console.error(`[EventBus] Error in listener for event '${String(event)}':`, err);
        }
      });
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

export const EventBus = new EventBusClass();
